import { useEffect, useRef } from 'react';

/**
 * Autoplay d'un carrousel, en pause au survol comme dans la maquette.
 *
 * Deux différences assumées avec le JS d'origine :
 * - il ne démarre pas si l'utilisateur a demandé `prefers-reduced-motion`
 *   (totalement absent de la maquette, qui a deux carrousels en autoplay) ;
 * - il se met aussi en pause quand le focus clavier entre dans le conteneur,
 *   sinon un lecteur au clavier voit la diapositive changer sous lui.
 */
const useAutoplay = (onTick, delay, containerRef, enabled = true) => {
  const savedTick = useRef(onTick);
  savedTick.current = onTick;

  useEffect(() => {
    if (!enabled || !delay) return undefined;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    let timer = null;
    const start = () => {
      if (timer === null) timer = setInterval(() => savedTick.current(), delay);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    start();

    const node = containerRef && containerRef.current;
    if (node) {
      node.addEventListener('mouseenter', stop);
      node.addEventListener('mouseleave', start);
      node.addEventListener('focusin', stop);
      node.addEventListener('focusout', start);
    }

    return () => {
      stop();
      if (node) {
        node.removeEventListener('mouseenter', stop);
        node.removeEventListener('mouseleave', start);
        node.removeEventListener('focusin', stop);
        node.removeEventListener('focusout', start);
      }
    };
  }, [delay, enabled, containerRef]);
};

export default useAutoplay;
